import { type ReactNode, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';

// Public-templates list hook. quiz-app now consumes the admin-free `quiz` OpenAPI
// group, so the admin `GET /admin/ai-tutor-templates` no longer exists in this doc
// and the old barrel collision (`useGetAllAiTutorTemplates1Suspense` binding to the
// admin module — see #2539) can no longer happen. Import stays on the generated
// module for clarity. Public endpoint: `GET /ai-tutor-templates`.
import { useGetAllAiTutorTemplatesSuspense } from '@/api/gen/ai-tutor-template/ai-tutor-template.gen';
import { useViewport } from '@/hooks';
import { ChevronLeftIcon } from '@/icons';
import { type TemplateAiTutor, useTemplateAiTutorGetter } from '@/shared/components/Chatbot';
import { useUpsellingGuard } from '@/shared/plan';
import { DialogBody, DialogHeader, IconButton, ResponsiveDialog } from '@/ui_v3';
import { UpgradeButton, useUpgradeToPro } from '../FeatureUpsellModal';
import { AiTutorDetail } from './AiTutorDetail';
import { AiTutorInstallIntro } from './AiTutorInstallIntro';
import { AiTutorList } from './AiTutorList';
interface Props {
  initialSelectedAiTutorId?: string;
  mode?: 'install' | 'upsell';
  onConfirmAiTutor?: (aiTutor: TemplateAiTutor) => void;
  onClose?: VoidFunction;
}
export const AiTutorInstall = ({
  initialSelectedAiTutorId,
  mode = 'install',
  onConfirmAiTutor
}: Props) => {
  const {
    t
  } = useTranslation(['build']);
  const {
    isMobile,
    isTabletSm
  } = useViewport();
  const isOnePane = isMobile || isTabletSm;
  const upsellingGuard = useUpsellingGuard();
  const upgradeToPro = useUpgradeToPro();
  const isUpsellMode = mode === 'upsell';
  const {
    data
  } = useGetAllAiTutorTemplatesSuspense();
  const templateAiTutorGetter = useTemplateAiTutorGetter();
  const aiTutors = data?.data.map(aiTutor => ({
    ...templateAiTutorGetter(aiTutor.id),
    isNew: aiTutor.hasNewTag
  })) ?? [];
  const [selectedAiTutorId, setSelectedAiTutorId] = useState(initialSelectedAiTutorId);
  const getAiTutor = (id: string) => aiTutors.find(tutor => tutor.id === id);
  const selectedAiTutor = selectedAiTutorId && getAiTutor(selectedAiTutorId);
  const handleUpgrade = () => upgradeToPro('click_ai_tutor_upselling_upgrade', 'aiTutor');

  // install/upsell 분기가 동일하게 쓰는 1팬 디테일 시트 셸. body·onClose만 분기한다.
  const openAiTutorDetailSheet = (body: ReactNode, {
    onClose
  }: {
    onClose?: VoidFunction;
  } = {}) => {
    overlay.open(({
      unmount
    }) => <ResponsiveDialog className="h-full mobile-lg:max-h-[670px]" size="xl" onClose={onClose ?? unmount}>
        <DialogHeader className="shrink-0" title={t('build:sectionAiTutor.add')} hasCloseButton leftSlot={<IconButton variant="transparent" intent="neutral" onClick={unmount}>
              <ChevronLeftIcon size="md" />
            </IconButton>} onClose={onClose ?? unmount} />
        <DialogBody className="h-auto min-h-0 flex-1 overflow-hidden">
          {body}
        </DialogBody>
      </ResponsiveDialog>);
  };
  const selectAiTutor = (id: string) => {
    if (isUpsellMode) {
      setSelectedAiTutorId(id);
      if (isOnePane) {
        openAiTutorDetailSheet(<div className="flex h-full min-w-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
              <AiTutorDetail aiTutor={getAiTutor(id)!} showConfirmButton={false} />
            </div>
            <UpgradeButtonContainer onClick={handleUpgrade} />
          </div>);
      }
      return;
    }
    upsellingGuard(() => {
      setSelectedAiTutorId(id);
      if (isOnePane) {
        openAiTutorDetailSheet(<AiTutorDetail aiTutor={getAiTutor(id)!} onConfirmAiTutor={onConfirmAiTutor} />, {
          onClose: overlay.unmountAll
        });
      }
    }, {
      feature: 'AI_TUTOR',
      from: 'aiTutor'
    });
  };
  if (isOnePane && isUpsellMode) {
    return <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <AiTutorInstallIntro showAddButton={false} />
        </div>
        <UpgradeButtonContainer onClick={handleUpgrade} />
      </div>;
  }
  return <div className="flex h-full flex-col tablet-sm:flex-row" data-sentry-component="AiTutorInstall" data-sentry-source-file="AiTutorInstall.tsx">
      <AiTutorList aiTutors={aiTutors} selectedAiTutorId={selectedAiTutorId} onSelectAiTutor={selectAiTutor} title={isUpsellMode ? t('build:aiTutorInstall.selectTutorTitle') : undefined} data-sentry-element="AiTutorList" data-sentry-source-file="AiTutorInstall.tsx" />
      {!isOnePane && <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col shadow-sm">
          <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
            {selectedAiTutor ? <AiTutorDetail aiTutor={selectedAiTutor} onConfirmAiTutor={onConfirmAiTutor} showConfirmButton={!isUpsellMode} /> : <AiTutorInstallIntro showAddButton={!isUpsellMode} />}
          </div>
          {isUpsellMode && <UpgradeButtonContainer onClick={handleUpgrade} />}
        </div>}
    </div>;
};
const UpgradeButtonContainer = ({
  onClick
}: {
  onClick: VoidFunction;
}) => {
  return <div className="shrink-0 bg-white px-lg pb-lg pt-xs" data-sentry-component="UpgradeButtonContainer" data-sentry-source-file="AiTutorInstall.tsx">
      <UpgradeButton onClick={onClick} data-sentry-element="UpgradeButton" data-sentry-source-file="AiTutorInstall.tsx" />
    </div>;
};